<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useFormatStore } from '@/stores/formatStore'

const props = defineProps({
  mandatoryAmenities: { type: Array, default: () => [] },
  optionalAmenities: { type: Array, default: () => [] },
  selectedAmenities: { type: Array, default: () => [] }
})

const emit = defineEmits(['update:selectedAmenities'])

const { t } = useI18n()
const formatStore = useFormatStore()

function updateSelection(value: number[]) {
  emit('update:selectedAmenities', value)
}

function getAmenityLabel(amenity: any) {
  const uom = amenity.amenityUnit?.uomName
  if (uom) return uom.toLowerCase().startsWith('per ') ? uom : `Per ${uom}`
  return amenity.name
}
</script>


<template>
  <div v-if="mandatoryAmenities.length > 0" id="addons" class="container mt-12 flex flex-col gap-5">
    <!-- Mandatory extras -->
    <div class="responsive-title-4 font-semibold">
      {{ t('mandatoryExtras') }}
    </div>

    <div class="charges-subtitle responsive-body-large text-bw-darkGrey">
      {{ t('mandatoryExtrasText') }}
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 w-full">
      <div
          v-for="amenity in mandatoryAmenities"
          :key="amenity.id"
          class="flex flex-col"
      >
        <div class="flex border-2 border-primary-blue rounded-8xl px-6 py-7.5 h-100 ">
          <div class="gap-3 w-full">
            <div class="flex justify-between items-start">
              <div class="flex w-7/10 gap-x-3">
                <div class="flex items-start">

                  <v-checkbox
                      :model-value="true"
                      disabled
                      :ripple="false"
                      class="always-enabled"
                      base-color="#1B84FF"
                  />

                </div>
                <span
                    class="mt-auto mb-auto responsive-body-large font-semibold line-clamp-3">
                          {{ amenity.amenityGroup?.name || amenity.name }}
                        </span>
                <span class="responsive-body-medium text-bw-darkGrey"
                      v-if="amenity.amenityGroup">{{
                    amenity.name
                  }}</span>
              </div>
              <div
                  class="flex w-3/10 items-center justify-end text-end font-semibold responsive-body-medium m-auto">

                {{
                  amenity.price > 0 ? formatStore.formatCurrency(amenity.price) : t('noExtraCost')
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Optional extras -->
    <div
        v-if="optionalAmenities.length > 0"
        class="mt-12 flex flex-col gap-5"
        id="addons"
    >
      <div class="responsive-title-4 font-semibold">
        {{ t('optionalExtras') }}
      </div>

      <div class="responsive-body-large text-bw-darkGrey">
        {{ t('optionalExtrasText') }}
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        <div
            v-for="amenity in optionalAmenities"
            :key="amenity.id"
            class="amenity-card flex border rounded-8xl gap-x-3 px-6 pb-7.5 pt-10"
        >
          <v-checkbox
              :model-value="selectedAmenities"
              :value="amenity.id"
              base-color="#1B84FF"
              @update:modelValue="updateSelection"
          />

          <div class="flex flex-col w-full">
            <div class="flex justify-between items-start">
              <div class="flex flex-col w-7/10">
                <span class="responsive-body-large font-semibold">
                  {{ amenity.amenityGroup?.name || amenity.name }}
                </span>
                <span class="responsive-body-medium text-bw-darkGrey">
                  {{ getAmenityLabel(amenity) }}
                </span>
                <span class="responsive-body-medium text-bw-darkGrey">
                  {{ t('yacht.amenities.onSpot') }}
                </span>
              </div>

              <div class="responsive-body-medium font-semibold text-end w-3/10">
                {{
                  amenity.price > 0
                      ? formatStore.formatCurrency(amenity.price)
                      : t('noExtraCost')
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<style scoped>

.amenity-card {
  display: flex;
  flex-direction: row;
  align-items: stretch;
}

.amenity-card:has(input:checked) {
  border-color: transparent !important;
  outline: solid 2px #1B84FF !important;
}

</style>